import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { 
  Bot, ChevronRight, ChevronLeft, Send, 
  Bell, CheckCircle, Clock, AlertTriangle, 
  Phone, DollarSign, Calendar, RefreshCw,
  MessageSquare, Sparkles, TrendingUp, Users, FileText,
  UserCheck, AlertOctagon, ClipboardList, Truck, Search,
  HardHat, CreditCard, Receipt, Wallet, AlertCircle, PiggyBank,
  Maximize2, Minimize2, Mail, PhoneCall, CalendarPlus, CheckSquare,
  X, Loader2, Zap, Eye, Edit3
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AIAssistant = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState('leads'); // 'leads', 'clients', 'projects', 'accounting', or 'actions'
  const [leadTasks, setLeadTasks] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [accountingTasks, setAccountingTasks] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [leadStats, setLeadStats] = useState({});
  const [clientStats, setClientStats] = useState({});
  const [projectStats, setProjectStats] = useState({});
  const [accountingStats, setAccountingStats] = useState({});
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Quick Action State
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [quickActionType, setQuickActionType] = useState(null);
  const [quickActionData, setQuickActionData] = useState({});
  const [executingAction, setExecutingAction] = useState(false);
  
  // Email Draft State
  const [draftedEmail, setDraftedEmail] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  // Fetch tasks on mount and periodically
  useEffect(() => {
    if (isActive) {
      fetchAllTasks();
      const interval = setInterval(fetchAllTasks, 60000); // Refresh every minute
      return () => clearInterval(interval);
    }
  }, [isActive]);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchAllTasks = async () => {
    setLoading(true);
    try {
      // Fetch all 5 task types in parallel
      const [leadResponse, clientResponse, projectResponse, accountingResponse, actionsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ai-assistant/lead-tasks`),
        fetch(`${BACKEND_URL}/api/ai-assistant/client-tasks`),
        fetch(`${BACKEND_URL}/api/ai-assistant/project-tasks`),
        fetch(`${BACKEND_URL}/api/ai-assistant/accounting-tasks`),
        fetch(`${BACKEND_URL}/api/ai-assistant/pending-actions`)
      ]);
      
      if (leadResponse.ok) {
        const data = await leadResponse.json();
        setLeadTasks(data.tasks || []);
        setLeadStats(data.by_type || {});
      }
      
      if (clientResponse.ok) {
        const data = await clientResponse.json();
        setClientTasks(data.tasks || []);
        setClientStats(data.by_type || {});
      }
      
      if (projectResponse.ok) {
        const data = await projectResponse.json();
        setProjectTasks(data.tasks || []);
        setProjectStats(data.by_type || {});
      }
      
      if (accountingResponse.ok) {
        const data = await accountingResponse.json();
        setAccountingTasks(data.tasks || []);
        setAccountingStats(data.by_type || {});
      }
      
      if (actionsResponse.ok) {
        const data = await actionsResponse.json();
        setPendingActions(data.actions || []);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendChat = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || sendingChat) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setSendingChat(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, context: activeTab })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: language === 'es' 
            ? 'Lo siento, hubo un error. Por favor intenta de nuevo.' 
            : 'Sorry, there was an error. Please try again.' 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: language === 'es' 
          ? 'Error de conexión. Por favor intenta de nuevo.' 
          : 'Connection error. Please try again.' 
      }]);
    } finally {
      setSendingChat(false);
      inputRef.current?.focus();
    }
  };

  const createCalendarTask = async (task) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/create-calendar-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: task.title,
          description: task.description,
          task_type: task.task_type || 'follow_up',
          date: task.due_date,
          lead_id: task.lead_id,
          client_id: task.client_id,
          priority: task.priority
        })
      });

      if (response.ok) {
        toast.success(language === 'es' ? 'Tarea agregada al calendario' : 'Task added to calendar');
      } else {
        toast.error(language === 'es' ? 'Error al crear tarea' : 'Error creating task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(language === 'es' ? 'Error al crear tarea' : 'Error creating task');
    }
  };

  // Quick Action: Schedule Inspection
  const scheduleInspection = async (data) => {
    setExecutingAction(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/schedule-inspection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || (language === 'es' ? 'Inspección programada' : 'Inspection scheduled'));
        setShowQuickActions(false);
        setQuickActionData({});
        fetchAllTasks();
      } else {
        toast.error(language === 'es' ? 'Error al programar inspección' : 'Error scheduling inspection');
      }
    } catch (error) {
      console.error('Error scheduling inspection:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setExecutingAction(false);
    }
  };

  // Quick Action: Draft Email
  const draftEmail = async (data) => {
    setExecutingAction(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/draft-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setDraftedEmail(result.email);
          setShowEmailPreview(true);
          setShowQuickActions(false);
        } else {
          toast.error(result.message || 'Error drafting email');
        }
      } else {
        toast.error(language === 'es' ? 'Error al generar email' : 'Error generating email');
      }
    } catch (error) {
      console.error('Error drafting email:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setExecutingAction(false);
    }
  };

  // Quick Action: Send Email
  const sendEmail = async () => {
    if (!draftedEmail) return;
    setExecutingAction(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/send-client-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_email: draftedEmail.to,
          client_name: quickActionData.client_name,
          client_id: quickActionData.client_id,
          subject: draftedEmail.subject,
          body: draftedEmail.body
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message || (language === 'es' ? 'Email enviado' : 'Email sent'));
          setShowEmailPreview(false);
          setDraftedEmail(null);
          setQuickActionData({});
        } else {
          toast.error(result.message || 'Failed to send email');
        }
      } else {
        toast.error(language === 'es' ? 'Error al enviar email' : 'Error sending email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setExecutingAction(false);
    }
  };

  // Quick Action: Send Notification to Admin
  const sendNotification = async (data) => {
    setExecutingAction(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/send-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message || (language === 'es' ? 'Notificación enviada' : 'Notification sent'));
          setShowQuickActions(false);
          setQuickActionData({});
        } else {
          toast.error(result.message || 'Failed to send notification');
        }
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setExecutingAction(false);
    }
  };

  // Quick Action: Request Call Transfer
  const requestCallTransfer = async (data) => {
    setExecutingAction(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/transfer-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(
          language === 'es' 
            ? `Llamar al: ${result.company_phone}` 
            : `Transfer to: ${result.company_phone}`
        );
        // Show the phone number prominently
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `📞 **Call Transfer**\n\nCompany Phone: **${result.company_phone}**\n\nClient: ${data.client_name}\nClient Phone: ${data.client_phone || 'N/A'}\n\nThe admin has been notified.`
        }]);
        setShowQuickActions(false);
        setQuickActionData({});
      }
    } catch (error) {
      console.error('Error requesting call transfer:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setExecutingAction(false);
    }
  };

  // Approve or Reject Pending Action
  const handlePendingAction = async (actionId, status, adminNotes = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/pending-actions/${actionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_notes: adminNotes })
      });

      if (response.ok) {
        toast.success(
          status === 'approved' 
            ? (language === 'es' ? 'Acción aprobada' : 'Action approved')
            : (language === 'es' ? 'Acción rechazada' : 'Action rejected')
        );
        fetchAllTasks();
      }
    } catch (error) {
      console.error('Error updating pending action:', error);
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  // Open Quick Action Modal
  const openQuickAction = (type, taskData = {}) => {
    setQuickActionType(type);
    setQuickActionData({
      client_id: taskData.client_id || taskData.lead_id,
      client_name: taskData.client_name || taskData.lead_name || '',
      client_email: taskData.client_email || '',
      client_phone: taskData.client_phone || '',
      ...taskData
    });
    setShowQuickActions(true);
  };

  const getLeadTaskIcon = (taskType) => {
    switch (taskType) {
      case 'new_lead': return <Sparkles className="h-4 w-4 text-green-500" />;
      case 'no_contact': return <Phone className="h-4 w-4 text-red-500" />;
      case 'estimate_sent': return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'lead_aging': return <Clock className="h-4 w-4 text-orange-500" />;
      case 'presentation_made': return <TrendingUp className="h-4 w-4 text-purple-500" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getClientTaskIcon = (taskType) => {
    switch (taskType) {
      case 'new_client': return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'pending_change_order': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'overdue_invoice': return <AlertOctagon className="h-4 w-4 text-red-500" />;
      case 'client_inactive': return <Clock className="h-4 w-4 text-orange-500" />;
      case 'outstanding_balance': return <DollarSign className="h-4 w-4 text-yellow-600" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getProjectTaskIcon = (taskType) => {
    switch (taskType) {
      case 'next_action': return <ClipboardList className="h-4 w-4 text-blue-500" />;
      case 'permit_followup': return <Search className="h-4 w-4 text-purple-500" />;
      case 'inspection_pending': return <HardHat className="h-4 w-4 text-orange-500" />;
      case 'material_delivery': return <Truck className="h-4 w-4 text-green-500" />;
      case 'material_overdue': return <Truck className="h-4 w-4 text-red-500" />;
      case 'final_payment': return <CreditCard className="h-4 w-4 text-green-600" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getAccountingTaskIcon = (taskType) => {
    switch (taskType) {
      case 'invoice_due_soon': return <Receipt className="h-4 w-4 text-yellow-500" />;
      case 'invoice_overdue': return <AlertOctagon className="h-4 w-4 text-red-500" />;
      case 'partial_payment': return <Wallet className="h-4 w-4 text-blue-500" />;
      case 'vendor_payment_due': return <CreditCard className="h-4 w-4 text-orange-500" />;
      case 'vendor_payment_overdue': return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'budget_warning': return <PiggyBank className="h-4 w-4 text-yellow-600" />;
      case 'budget_critical': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default: return <DollarSign className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getCurrentTasks = () => {
    switch (activeTab) {
      case 'leads': return leadTasks;
      case 'clients': return clientTasks;
      case 'projects': return projectTasks;
      case 'accounting': return accountingTasks;
      case 'actions': return pendingActions;
      default: return [];
    }
  };

  const getCurrentStats = () => {
    switch (activeTab) {
      case 'leads': return leadStats;
      case 'clients': return clientStats;
      case 'projects': return projectStats;
      case 'accounting': return accountingStats;
      case 'actions': return { pending: pendingActions.length };
      default: return {};
    }
  };

  const getTaskIcon = (task) => {
    switch (activeTab) {
      case 'leads': return getLeadTaskIcon(task.task_type);
      case 'clients': return getClientTaskIcon(task.task_type);
      case 'projects': return getProjectTaskIcon(task.task_type);
      case 'accounting': return getAccountingTaskIcon(task.task_type);
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTaskLink = (task) => {
    if (activeTab === 'leads') return `/leads/${task.lead_id}`;
    return `/clients/${task.client_id}`;
  };

  const getTaskName = (task) => {
    if (activeTab === 'leads') return task.lead_name;
    if (activeTab === 'accounting' && task.vendor_name) return task.vendor_name;
    return task.client_name;
  };

  const currentTasks = getCurrentTasks();
  const currentStats = getCurrentStats();
  const totalTasks = leadTasks.length + clientTasks.length + projectTasks.length + accountingTasks.length + pendingActions.length;

  // Collapsed state - just show toggle button
  if (!isOpen) {
    return (
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-l-lg rounded-r-none h-24 px-2 shadow-lg"
          style={{ backgroundColor: COMPANY.primaryColor }}
          data-testid="ai-assistant-open-btn"
        >
          <div className="flex flex-col items-center gap-1">
            <Bot className="h-5 w-5 text-white" />
            <ChevronLeft className="h-4 w-4 text-white" />
            {totalTasks > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5">
                {totalTasks}
              </Badge>
            )}
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div 
      className={`fixed right-0 top-0 h-full bg-white border-l shadow-xl z-50 flex flex-col transition-all duration-300 ${isExpanded ? 'w-[600px]' : 'w-80'}`}
      data-testid="ai-assistant-panel"
    >
      {/* Header */}
      <div 
        className="p-3 border-b flex items-center justify-between"
        style={{ backgroundColor: COMPANY.primaryColor }}
      >
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-white" />
          <span className="font-semibold text-white">
            {language === 'es' ? 'Asistente AI' : 'AI Assistant'}
          </span>
          {isActive && (
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:bg-white/20 h-7 px-2"
            title={isExpanded ? (language === 'es' ? 'Reducir' : 'Collapse') : (language === 'es' ? 'Expandir' : 'Expand')}
          >
            {isExpanded ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsActive(!isActive)}
            className="text-white hover:bg-white/20 h-7 px-2"
            title={isActive ? (language === 'es' ? 'Desactivar' : 'Deactivate') : (language === 'es' ? 'Activar' : 'Activate')}
          >
            {isActive ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAllTasks}
            className="text-white hover:bg-white/20 h-7 px-2"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
            className="text-white hover:bg-white/20 h-7 px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {!isActive ? (
        <div className="flex-1 flex items-center justify-center p-4 text-center">
          <div>
            <Bot className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 text-sm">
              {language === 'es' 
                ? 'Asistente desactivado. Haz clic en el icono de check para activar.' 
                : 'Assistant deactivated. Click the check icon to activate.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Tab Switcher - 5 tabs */}
          <div className="flex border-b bg-gray-50">
            <button
              onClick={() => setActiveTab('leads')}
              className={`flex-1 py-1.5 px-1 text-[10px] font-medium flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === 'leads' 
                  ? 'bg-white border-b-2 text-amber-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'leads' ? { borderBottomColor: COMPANY.primaryColor } : {}}
              data-testid="ai-tab-leads"
            >
              <TrendingUp className="h-3.5 w-3.5" />
              <span>{language === 'es' ? 'Prosp.' : 'Leads'}</span>
              {leadTasks.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-amber-100 text-amber-700">
                  {leadTasks.length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex-1 py-1.5 px-1 text-[10px] font-medium flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === 'clients' 
                  ? 'bg-white border-b-2 text-amber-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'clients' ? { borderBottomColor: COMPANY.primaryColor } : {}}
              data-testid="ai-tab-clients"
            >
              <Users className="h-3.5 w-3.5" />
              <span>{language === 'es' ? 'Client.' : 'Clients'}</span>
              {clientTasks.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-blue-100 text-blue-700">
                  {clientTasks.length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex-1 py-1.5 px-1 text-[10px] font-medium flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === 'projects' 
                  ? 'bg-white border-b-2 text-amber-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'projects' ? { borderBottomColor: COMPANY.primaryColor } : {}}
              data-testid="ai-tab-projects"
            >
              <HardHat className="h-3.5 w-3.5" />
              <span>{language === 'es' ? 'Proy.' : 'Proj.'}</span>
              {projectTasks.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-green-100 text-green-700">
                  {projectTasks.length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('accounting')}
              className={`flex-1 py-1.5 px-1 text-[10px] font-medium flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === 'accounting' 
                  ? 'bg-white border-b-2 text-amber-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'accounting' ? { borderBottomColor: COMPANY.primaryColor } : {}}
              data-testid="ai-tab-accounting"
            >
              <DollarSign className="h-3.5 w-3.5" />
              <span>{language === 'es' ? 'Cont.' : 'Acct.'}</span>
              {accountingTasks.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-purple-100 text-purple-700">
                  {accountingTasks.length}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`flex-1 py-1.5 px-1 text-[10px] font-medium flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === 'actions' 
                  ? 'bg-white border-b-2 text-amber-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === 'actions' ? { borderBottomColor: COMPANY.primaryColor } : {}}
              data-testid="ai-tab-actions"
            >
              <Zap className="h-3.5 w-3.5" />
              <span>{language === 'es' ? 'Acc.' : 'Act.'}</span>
              {pendingActions.length > 0 && (
                <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-red-100 text-red-700">
                  {pendingActions.length}
                </Badge>
              )}
            </button>
          </div>

          {/* Quick Action Buttons */}
          <div className="p-2 border-b bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex gap-1.5 flex-wrap justify-center">
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px] bg-white hover:bg-blue-50 border-blue-200"
                onClick={() => openQuickAction('schedule_inspection')}
                data-testid="quick-action-inspection"
              >
                <CalendarPlus className="h-3 w-3 mr-1 text-blue-600" />
                {language === 'es' ? 'Inspección' : 'Inspection'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px] bg-white hover:bg-green-50 border-green-200"
                onClick={() => openQuickAction('draft_email')}
                data-testid="quick-action-email"
              >
                <Mail className="h-3 w-3 mr-1 text-green-600" />
                {language === 'es' ? 'Email' : 'Email'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px] bg-white hover:bg-purple-50 border-purple-200"
                onClick={() => openQuickAction('transfer_call')}
                data-testid="quick-action-call"
              >
                <PhoneCall className="h-3 w-3 mr-1 text-purple-600" />
                {language === 'es' ? 'Llamar' : 'Call'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px] bg-white hover:bg-amber-50 border-amber-200"
                onClick={() => openQuickAction('send_notification')}
                data-testid="quick-action-notify"
              >
                <Bell className="h-3 w-3 mr-1 text-amber-600" />
                {language === 'es' ? 'Notificar' : 'Notify'}
              </Button>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="p-2 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  {language === 'es' ? 'Tareas' : 'Tasks'}
                </h3>
                <Badge variant="outline" className="text-xs">
                  {currentTasks.length}
                </Badge>
              </div>
              {/* Task type summary icons */}
              {currentTasks.length > 0 && (
                <div className="flex gap-1.5 mt-1.5 text-xs flex-wrap">
                  {activeTab === 'leads' && (
                    <>
                      {currentStats.new_lead > 0 && (
                        <span className="flex items-center gap-0.5 text-green-600" title="New Leads">
                          <Sparkles className="h-3 w-3" /> {currentStats.new_lead}
                        </span>
                      )}
                      {currentStats.no_contact > 0 && (
                        <span className="flex items-center gap-0.5 text-red-600" title="No Contact">
                          <Phone className="h-3 w-3" /> {currentStats.no_contact}
                        </span>
                      )}
                      {currentStats.estimate_sent > 0 && (
                        <span className="flex items-center gap-0.5 text-blue-600" title="Estimate Sent">
                          <MessageSquare className="h-3 w-3" /> {currentStats.estimate_sent}
                        </span>
                      )}
                      {currentStats.lead_aging > 0 && (
                        <span className="flex items-center gap-0.5 text-orange-600" title="Lead Aging">
                          <Clock className="h-3 w-3" /> {currentStats.lead_aging}
                        </span>
                      )}
                    </>
                  )}
                  {activeTab === 'clients' && (
                    <>
                      {currentStats.new_client > 0 && (
                        <span className="flex items-center gap-0.5 text-green-600" title="New Clients">
                          <UserCheck className="h-3 w-3" /> {currentStats.new_client}
                        </span>
                      )}
                      {currentStats.overdue_invoice > 0 && (
                        <span className="flex items-center gap-0.5 text-red-600" title="Overdue Invoices">
                          <AlertOctagon className="h-3 w-3" /> {currentStats.overdue_invoice}
                        </span>
                      )}
                      {currentStats.outstanding_balance > 0 && (
                        <span className="flex items-center gap-0.5 text-yellow-600" title="Outstanding Balance">
                          <DollarSign className="h-3 w-3" /> {currentStats.outstanding_balance}
                        </span>
                      )}
                    </>
                  )}
                  {activeTab === 'projects' && (
                    <>
                      {currentStats.next_action > 0 && (
                        <span className="flex items-center gap-0.5 text-blue-600" title="Next Action">
                          <ClipboardList className="h-3 w-3" /> {currentStats.next_action}
                        </span>
                      )}
                      {currentStats.permit_followup > 0 && (
                        <span className="flex items-center gap-0.5 text-purple-600" title="Permit Follow-up">
                          <Search className="h-3 w-3" /> {currentStats.permit_followup}
                        </span>
                      )}
                      {currentStats.inspection_pending > 0 && (
                        <span className="flex items-center gap-0.5 text-orange-600" title="Inspection Pending">
                          <HardHat className="h-3 w-3" /> {currentStats.inspection_pending}
                        </span>
                      )}
                    </>
                  )}
                  {activeTab === 'accounting' && (
                    <>
                      {currentStats.invoice_overdue > 0 && (
                        <span className="flex items-center gap-0.5 text-red-600" title="Overdue Invoices">
                          <AlertOctagon className="h-3 w-3" /> {currentStats.invoice_overdue}
                        </span>
                      )}
                      {currentStats.invoice_due_soon > 0 && (
                        <span className="flex items-center gap-0.5 text-yellow-600" title="Due Soon">
                          <Receipt className="h-3 w-3" /> {currentStats.invoice_due_soon}
                        </span>
                      )}
                      {currentStats.vendor_payment_overdue > 0 && (
                        <span className="flex items-center gap-0.5 text-red-700" title="Vendor Overdue">
                          <AlertCircle className="h-3 w-3" /> {currentStats.vendor_payment_overdue}
                        </span>
                      )}
                      {currentStats.vendor_payment_due > 0 && (
                        <span className="flex items-center gap-0.5 text-orange-600" title="Vendor Due">
                          <CreditCard className="h-3 w-3" /> {currentStats.vendor_payment_due}
                        </span>
                      )}
                      {(currentStats.budget_warning > 0 || currentStats.budget_critical > 0) && (
                        <span className="flex items-center gap-0.5 text-yellow-700" title="Budget Alerts">
                          <PiggyBank className="h-3 w-3" /> {(currentStats.budget_warning || 0) + (currentStats.budget_critical || 0)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <ScrollArea className="flex-1 p-2">
              {loading && currentTasks.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : currentTasks.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">
                    {language === 'es' ? '¡Todo al día!' : 'All caught up!'}
                  </p>
                  <p className="text-xs mt-1 text-gray-300">
                    {activeTab === 'leads' && (language === 'es' ? 'No hay tareas de prospectos' : 'No lead tasks')}
                    {activeTab === 'clients' && (language === 'es' ? 'No hay tareas de clientes' : 'No client tasks')}
                    {activeTab === 'projects' && (language === 'es' ? 'No hay tareas de proyectos' : 'No project tasks')}
                    {activeTab === 'accounting' && (language === 'es' ? 'No hay alertas de contabilidad' : 'No accounting alerts')}
                    {activeTab === 'actions' && (language === 'es' ? 'No hay acciones pendientes' : 'No pending actions')}
                  </p>
                </div>
              ) : activeTab === 'actions' ? (
                // Render pending actions differently
                <div className="space-y-2">
                  {pendingActions.slice(0, 8).map((action) => (
                    <Card 
                      key={action.id} 
                      className={`p-2 border ${
                        action.priority === 'high' ? 'border-red-200 bg-red-50' : 
                        action.priority === 'medium' ? 'border-yellow-200 bg-yellow-50' : 
                        'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Zap className={`h-4 w-4 ${
                          action.priority === 'high' ? 'text-red-500' : 
                          action.priority === 'medium' ? 'text-yellow-500' : 
                          'text-gray-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-medium text-xs truncate">
                              {action.client_name}
                            </span>
                            <Badge variant="outline" className="text-[9px]">
                              {action.action_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                            {action.description}
                          </p>
                          {action.ai_reasoning && (
                            <p className="text-[10px] text-blue-600 mt-0.5 italic">
                              AI: {action.ai_reasoning}
                            </p>
                          )}
                          <div className="flex items-center gap-1 mt-1.5">
                            <Button
                              size="sm"
                              variant="default"
                              className="h-5 px-2 text-[10px] bg-green-600 hover:bg-green-700"
                              onClick={() => handlePendingAction(action.id, 'approved')}
                            >
                              <CheckCircle className="h-3 w-3 mr-0.5" />
                              {language === 'es' ? 'Aprobar' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-5 px-2 text-[10px] border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handlePendingAction(action.id, 'rejected')}
                            >
                              <X className="h-3 w-3 mr-0.5" />
                              {language === 'es' ? 'Rechazar' : 'Reject'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {currentTasks.slice(0, 8).map((task) => (
                    <Card 
                      key={task.id} 
                      className={`p-2 cursor-pointer hover:shadow-md transition-shadow border ${getPriorityColor(task.priority)}`}
                    >
                      <div className="flex items-start gap-2">
                        {getTaskIcon(task)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <Link 
                              to={getTaskLink(task)}
                              className="font-medium text-xs truncate hover:underline"
                              style={{ color: COMPANY.primaryColor }}
                            >
                              {getTaskName(task)}
                            </Link>
                            {task.amount !== undefined && (
                              <span className={`text-xs font-medium whitespace-nowrap ${task.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatCurrency(Math.abs(task.amount))}
                              </span>
                            )}
                            {task.progress_pct !== undefined && (
                              <span className="text-xs text-blue-600 font-medium whitespace-nowrap">
                                {task.progress_pct}%
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                            {task.description}
                          </p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-5 px-1.5 text-[10px]"
                              onClick={(e) => {
                                e.preventDefault();
                                createCalendarTask(task);
                              }}
                            >
                              <Calendar className="h-3 w-3 mr-0.5" />
                              {language === 'es' ? 'Agendar' : 'Schedule'}
                            </Button>
                            {/* Quick action buttons for tasks */}
                            {task.client_email && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 px-1.5 text-[10px] text-green-600"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openQuickAction('draft_email', task);
                                }}
                              >
                                <Mail className="h-3 w-3 mr-0.5" />
                                {language === 'es' ? 'Email' : 'Email'}
                              </Button>
                            )}
                            {task.client_phone && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 px-1.5 text-[10px] text-purple-600"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openQuickAction('transfer_call', task);
                                }}
                              >
                                <PhoneCall className="h-3 w-3 mr-0.5" />
                                {language === 'es' ? 'Llamar' : 'Call'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {currentTasks.length > 8 && (
                    <p className="text-xs text-center text-gray-400 py-1">
                      +{currentTasks.length - 8} {language === 'es' ? 'más' : 'more'}
                    </p>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat Section */}
          <div className="border-t bg-gray-50">
            <div className="p-1.5 border-b">
              <h3 className="font-medium text-xs text-gray-700 flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                {language === 'es' ? 'Chat' : 'Chat'}
              </h3>
            </div>
            
            {/* Chat Messages */}
            <ScrollArea className="h-32 p-2">
              {chatMessages.length === 0 ? (
                <div className="text-center py-3 text-gray-400 text-xs">
                  <p>{language === 'es' ? 'Pregúntame...' : 'Ask me anything...'}</p>
                  <p className="mt-1 text-gray-300 text-[10px]">
                    {activeTab === 'leads' && '"Which leads need follow-up?"'}
                    {activeTab === 'clients' && '"Outstanding balance?"'}
                    {activeTab === 'projects' && '"Projects waiting for permits?"'}
                    {activeTab === 'accounting' && '"What invoices are overdue?"'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg text-xs ${
                        msg.role === 'user'
                          ? 'bg-amber-100 ml-4'
                          : 'bg-white border mr-4'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <Bot className="h-3 w-3 inline mr-1 text-amber-600" />
                      )}
                      <span className="whitespace-pre-wrap">{msg.content}</span>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Chat Input */}
            <form onSubmit={handleSendChat} className="p-2 border-t bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={language === 'es' ? 'Escribe...' : 'Type...'}
                  className="h-7 text-xs"
                  disabled={sendingChat}
                  data-testid="ai-chat-input"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={sendingChat || !chatInput.trim()}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="h-7 px-2"
                  data-testid="ai-chat-send-btn"
                >
                  {sendingChat ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Quick Action Modal */}
      {showQuickActions && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowQuickActions(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: COMPANY.primaryColor }}>
              <h3 className="font-semibold text-white flex items-center gap-2">
                {quickActionType === 'schedule_inspection' && <CalendarPlus className="h-5 w-5" />}
                {quickActionType === 'draft_email' && <Mail className="h-5 w-5" />}
                {quickActionType === 'transfer_call' && <PhoneCall className="h-5 w-5" />}
                {quickActionType === 'send_notification' && <Bell className="h-5 w-5" />}
                {quickActionType === 'schedule_inspection' && (language === 'es' ? 'Programar Inspección' : 'Schedule Inspection')}
                {quickActionType === 'draft_email' && (language === 'es' ? 'Redactar Email' : 'Draft Email')}
                {quickActionType === 'transfer_call' && (language === 'es' ? 'Información de Llamada' : 'Call Information')}
                {quickActionType === 'send_notification' && (language === 'es' ? 'Enviar Notificación' : 'Send Notification')}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowQuickActions(false)} className="text-white hover:bg-white/20">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Schedule Inspection Form */}
              {quickActionType === 'schedule_inspection' && (
                <>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Cliente' : 'Client'}</label>
                    <Input 
                      value={quickActionData.client_name || ''} 
                      onChange={e => setQuickActionData({...quickActionData, client_name: e.target.value})}
                      placeholder={language === 'es' ? 'Nombre del cliente' : 'Client name'}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Tipo de Inspección' : 'Inspection Type'}</label>
                    <select 
                      className="w-full p-2 border rounded-md text-sm"
                      value={quickActionData.inspection_type || 'general'}
                      onChange={e => setQuickActionData({...quickActionData, inspection_type: e.target.value})}
                    >
                      <option value="first">{language === 'es' ? 'Primera Inspección' : 'First Inspection'}</option>
                      <option value="progress">{language === 'es' ? 'Inspección de Progreso' : 'Progress Inspection'}</option>
                      <option value="final">{language === 'es' ? 'Inspección Final' : 'Final Inspection'}</option>
                      <option value="general">{language === 'es' ? 'General' : 'General'}</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">{language === 'es' ? 'Fecha' : 'Date'}</label>
                      <Input 
                        type="date"
                        value={quickActionData.date || new Date().toISOString().split('T')[0]}
                        onChange={e => setQuickActionData({...quickActionData, date: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">{language === 'es' ? 'Hora' : 'Time'}</label>
                      <Input 
                        type="time"
                        value={quickActionData.time || '09:00'}
                        onChange={e => setQuickActionData({...quickActionData, time: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Notas' : 'Notes'}</label>
                    <Textarea 
                      value={quickActionData.notes || ''} 
                      onChange={e => setQuickActionData({...quickActionData, notes: e.target.value})}
                      placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Draft Email Form */}
              {quickActionType === 'draft_email' && (
                <>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Cliente' : 'Client'}</label>
                    <Input 
                      value={quickActionData.client_name || ''} 
                      onChange={e => setQuickActionData({...quickActionData, client_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input 
                      type="email"
                      value={quickActionData.client_email || ''} 
                      onChange={e => setQuickActionData({...quickActionData, client_email: e.target.value})}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Tipo de Email' : 'Email Type'}</label>
                    <select 
                      className="w-full p-2 border rounded-md text-sm"
                      value={quickActionData.email_type || 'follow_up'}
                      onChange={e => setQuickActionData({...quickActionData, email_type: e.target.value})}
                    >
                      <option value="follow_up">{language === 'es' ? 'Seguimiento' : 'Follow-up'}</option>
                      <option value="reminder">{language === 'es' ? 'Recordatorio' : 'Reminder'}</option>
                      <option value="thank_you">{language === 'es' ? 'Agradecimiento' : 'Thank You'}</option>
                      <option value="appointment">{language === 'es' ? 'Cita/Inspección' : 'Appointment/Inspection'}</option>
                      <option value="payment">{language === 'es' ? 'Recordatorio de Pago' : 'Payment Reminder'}</option>
                      <option value="custom">{language === 'es' ? 'Personalizado' : 'Custom'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Contexto Adicional' : 'Additional Context'}</label>
                    <Textarea 
                      value={quickActionData.custom_message || ''} 
                      onChange={e => setQuickActionData({...quickActionData, custom_message: e.target.value})}
                      placeholder={language === 'es' ? 'Información adicional para el email...' : 'Additional information for the email...'}
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Transfer Call Info */}
              {quickActionType === 'transfer_call' && (
                <>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <PhoneCall className="h-12 w-12 mx-auto text-purple-600 mb-2" />
                    <p className="text-2xl font-bold text-purple-700">954-399-3278</p>
                    <p className="text-sm text-gray-600 mt-1">{language === 'es' ? 'Número de la Compañía' : 'Company Phone'}</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><strong>{language === 'es' ? 'Cliente:' : 'Client:'}</strong> {quickActionData.client_name || 'N/A'}</p>
                    <p><strong>{language === 'es' ? 'Teléfono del Cliente:' : 'Client Phone:'}</strong> {quickActionData.client_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Razón de la Llamada' : 'Call Reason'}</label>
                    <Textarea 
                      value={quickActionData.reason || ''} 
                      onChange={e => setQuickActionData({...quickActionData, reason: e.target.value})}
                      placeholder={language === 'es' ? 'Motivo de la llamada...' : 'Reason for the call...'}
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Send Notification Form */}
              {quickActionType === 'send_notification' && (
                <>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Asunto' : 'Subject'}</label>
                    <Input 
                      value={quickActionData.subject || ''} 
                      onChange={e => setQuickActionData({...quickActionData, subject: e.target.value})}
                      placeholder={language === 'es' ? 'Asunto de la notificación' : 'Notification subject'}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Tipo' : 'Type'}</label>
                    <select 
                      className="w-full p-2 border rounded-md text-sm"
                      value={quickActionData.notification_type || 'reminder'}
                      onChange={e => setQuickActionData({...quickActionData, notification_type: e.target.value})}
                    >
                      <option value="reminder">{language === 'es' ? 'Recordatorio' : 'Reminder'}</option>
                      <option value="follow_up">{language === 'es' ? 'Seguimiento' : 'Follow-up'}</option>
                      <option value="urgent">{language === 'es' ? 'Urgente' : 'Urgent'}</option>
                      <option value="payment">{language === 'es' ? 'Pago' : 'Payment'}</option>
                      <option value="inspection">{language === 'es' ? 'Inspección' : 'Inspection'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{language === 'es' ? 'Mensaje' : 'Message'}</label>
                    <Textarea 
                      value={quickActionData.message || ''} 
                      onChange={e => setQuickActionData({...quickActionData, message: e.target.value})}
                      placeholder={language === 'es' ? 'Mensaje de notificación...' : 'Notification message...'}
                      rows={3}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {language === 'es' 
                      ? 'Esta notificación se enviará a: info@jymegafcconstruction.net' 
                      : 'This notification will be sent to: info@jymegafcconstruction.net'}
                  </p>
                </>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowQuickActions(false)}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button 
                onClick={() => {
                  if (quickActionType === 'schedule_inspection') scheduleInspection(quickActionData);
                  if (quickActionType === 'draft_email') draftEmail(quickActionData);
                  if (quickActionType === 'transfer_call') requestCallTransfer(quickActionData);
                  if (quickActionType === 'send_notification') sendNotification(quickActionData);
                }}
                disabled={executingAction}
                style={{ backgroundColor: COMPANY.primaryColor }}
              >
                {executingAction ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {quickActionType === 'schedule_inspection' && (language === 'es' ? 'Programar' : 'Schedule')}
                {quickActionType === 'draft_email' && (language === 'es' ? 'Generar Email' : 'Generate Email')}
                {quickActionType === 'transfer_call' && (language === 'es' ? 'Notificar Transferencia' : 'Notify Transfer')}
                {quickActionType === 'send_notification' && (language === 'es' ? 'Enviar' : 'Send')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showEmailPreview && draftedEmail && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowEmailPreview(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between bg-green-600">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {language === 'es' ? 'Vista Previa del Email' : 'Email Preview'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEmailPreview(false)} className="text-white hover:bg-white/20">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{language === 'es' ? 'Para:' : 'To:'}</label>
                <p className="font-medium">{draftedEmail.to || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{language === 'es' ? 'Asunto:' : 'Subject:'}</label>
                <Input 
                  value={draftedEmail.subject}
                  onChange={e => setDraftedEmail({...draftedEmail, subject: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{language === 'es' ? 'Mensaje:' : 'Message:'}</label>
                <Textarea 
                  value={draftedEmail.body}
                  onChange={e => setDraftedEmail({...draftedEmail, body: e.target.value})}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEmailPreview(false)}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button variant="outline" onClick={() => draftEmail(quickActionData)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Regenerar' : 'Regenerate'}
              </Button>
              <Button 
                onClick={sendEmail}
                disabled={executingAction || !draftedEmail.to}
                className="bg-green-600 hover:bg-green-700"
              >
                {executingAction ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {language === 'es' ? 'Enviar Email' : 'Send Email'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
